import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { CalendarFilters } from "@/components/calendar/types";

const DEFAULT_FILTERS: CalendarFilters = {
  categoryId: undefined,
  uncategorized: undefined,
  priority: undefined,
  hideMultiDay: undefined,
};

function parseCalendarFilters(sp: URLSearchParams): CalendarFilters {
  // categoryId: CSV of UUIDs
  const categoryRaw = sp.get("categoryId");
  let categoryId: string[] | undefined;
  if (categoryRaw) {
    const ids = categoryRaw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    categoryId = ids.length > 0 ? ids : undefined;
  }

  // priority: single integer 1-3
  const priorityRaw = sp.get("priority");
  let priority: number | undefined;
  if (priorityRaw !== null) {
    const n = parseInt(priorityRaw, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= 3) priority = n;
  }

  const hideMultiDay = sp.get("hideMultiDay") === "true" ? true : undefined;
  const uncategorized = sp.get("uncategorized") === "true" ? true : undefined;

  return { categoryId, priority, hideMultiDay, uncategorized };
}

function filtersToSearchParams(
  filters: CalendarFilters,
  existing: URLSearchParams,
): URLSearchParams {
  const sp = new URLSearchParams(existing);

  if (filters.categoryId && filters.categoryId.length > 0) {
    sp.set("categoryId", filters.categoryId.join(","));
  } else {
    sp.delete("categoryId");
  }

  if (filters.priority !== undefined) {
    sp.set("priority", String(filters.priority));
  } else {
    sp.delete("priority");
  }

  if (filters.hideMultiDay === true) {
    sp.set("hideMultiDay", "true");
  } else {
    sp.delete("hideMultiDay");
  }

  if (filters.uncategorized === true) {
    sp.set("uncategorized", "true");
  } else {
    sp.delete("uncategorized");
  }

  return sp;
}

export interface UseCalendarFiltersResult {
  filters: CalendarFilters;
  setPatch: (patch: Partial<CalendarFilters>) => void;
  clearAll: () => void;
  isDefault: boolean;
}

/**
 * URL-bound filter state for the calendar view. Only manages categoryId and
 * priority; status is always hardcoded to CREATED by CalendarPage.
 */
export function useCalendarFilters(): UseCalendarFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseCalendarFilters(searchParams), [searchParams]);

  const setPatch = useCallback(
    (patch: Partial<CalendarFilters>) => {
      const next: CalendarFilters = { ...filters, ...patch };
      setSearchParams(filtersToSearchParams(next, searchParams), { replace: true });
    },
    [filters, searchParams, setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchParams(filtersToSearchParams(DEFAULT_FILTERS, searchParams), {
      replace: true,
    });
  }, [searchParams, setSearchParams]);

  const isDefault =
    !filters.categoryId &&
    filters.uncategorized !== true &&
    filters.priority === undefined &&
    filters.hideMultiDay !== true;

  return { filters, setPatch, clearAll, isDefault };
}
